import { DefaultEmptyTable } from '@/components/default-empty-table';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { TABLE } from '@airreading/automation-test/test-ids';
import { Column, ColumnHelper, flexRender, Table as ReactTable } from '@tanstack/react-table';

import { CSSProperties, ReactNode, useEffect, useState } from 'react';

export interface BaseEntityTableGroupKey {
  key: string;
}

export interface EntityTableGroupBy<RowData, GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  getGroupKey: (row: RowData) => GroupKey;
  renderDivider: (groupKey: GroupKey, rowCount: number) => ReactNode;
}

/**
 * A type representing a group of rows that share the same group key. We use this to separate table rows
 * into groups to render a divider between each group.
 */
interface RowGroup<T, GroupKey extends BaseEntityTableGroupKey> {
  groupKey: GroupKey;
  rows: T[];
}

/**
 * Groups rows sequentially by comparing adjacent group keys. Does not sort rows. Only splits them
 * into groups in their existing order. A new group starts whenever the current row's key differs
 * from the previous row's key.
 *
 * Because grouping is order-dependent, the `groupBy` logic must align with the table's sort order.
 * For example, grouping by a session's start day only makes sense when the table is sorted by start
 * instant. Otherwise rows belonging to the same group may be split across pages or interleaved with
 * other groups, producing a confusing UX.
 */
function groupRowsByKey<T, GroupKey extends BaseEntityTableGroupKey>(
  items: T[],
  getGroupKey: (item: T) => GroupKey,
): RowGroup<T, GroupKey>[] {
  return items.reduce<RowGroup<T, GroupKey>[]>((groups, item) => {
    const groupKey = getGroupKey(item);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.groupKey.key === groupKey.key) {
      lastGroup.rows.push(item);
    } else {
      groups.push({ groupKey, rows: [item] });
    }
    return groups;
  }, []);
}

export interface EntityTableProps<RowData, GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  table: ReactTable<RowData>;
  filters?: {
    filterLeftComponents: ReactNode;
  };
  filterBarClassName?: string;
  filterLeftClassName?: string;
  bulkActionsMenuItems?: ReactNode;
  topBarRightComponents?: ReactNode;
  emptyState?: ReactNode;
  errorState?: ReactNode;
  loadingState?: ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  showPagination?: boolean;
  paginationOptions?: number[];
  paginationLeftComponents?: ReactNode;
  rowClassName?: string;
  headerClassName?: string;
  emptyStateClassName?: string;
  topBarBottomComponents?: ReactNode;
  onSelectAllPagesChange?: (isSelectAllActive: boolean) => void;
  isSelectAllPagesActive?: boolean;
  totalRowCount?: number;
  hideEndMessage?: boolean;
  tableKey?: string;
  autoManagedColumnIds?: string[];
  rowTestId?: (rowId: string, rowIndex: number) => string;
  cellTestId?: (rowId: string, columnId: string, rowIndex: number) => string | undefined;
  endMessageTestId?: string;
  bulkActionsTestId?: string;
  'data-testid'?: string;
  groupBy?: EntityTableGroupBy<RowData, GroupKey>;
}

import { ArrowLeftFromLine, ArrowRightFromLine, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { TableBulkActionsMenu } from '@/components/table/table-bulk-actions-menu';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCurrentRowSelectionCount } from '@/lib/bulk-selection';
import { cn } from '@/lib/tailwind-utils';
import { useLocation } from '@tanstack/react-router';
import { useLocalStorage } from 'usehooks-ts';
import { TableHead } from './table-head';

export interface EntityTablePaginationProps<TData> {
  table: ReactTable<TData>;
  options?: number[];
  onSelectAllPagesChange?: (isSelectAllActive: boolean) => void;
  isSelectAllPagesActive?: boolean;
  totalRowCount?: number;
  leftComponents?: ReactNode;
}

//These are the important styles to make sticky column pinning work!
//Apply styles like this using your CSS strategy of choice with this kind of logic to head cells, data cells, footer cells, etc.
//View the index.css file for more needed styles such as border-collapse: separate
export const getCommonPinningStyles = <T,>(column: Column<T>): CSSProperties => {
  const isPinned = column.getIsPinned();

  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');

  return {
    boxShadow: isLastLeftPinnedColumn ? '-4px 0 4px -4px gray inset' : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    overflow: isPinned ? 'hidden' : undefined,
    position: isPinned ? 'sticky' : undefined,
    width: isPinned ? column.getSize() : undefined,
    zIndex: isPinned ? 1 : 0,
  };
};

type PinnedComponentProps<TData, TValue> = {
  pinned: 'left' | 'right';
  children: React.ReactNode;
  column: Column<TData, TValue>;
};

export type PinnedColumnsState = Record<string, [string, 'left'][]>;

export const PinnedComponent = <T, K>(props: PinnedComponentProps<T, K>) => {
  useEffect(() => {
    props.column.pin(props.pinned);
  }, [props.column, props.pinned]);

  return props.children;
};

// eslint-disable-next-line react-refresh/only-export-components
export function createTableRowSelectionCell<TData>(
  columnHelper: ColumnHelper<TData>,
  options?: {
    isSelectAllPagesActive?: boolean;
    onSelectAllPagesChange?: (isSelectAllActive: boolean) => void;
    isPinned?: boolean;
    selectAllTestId?: string;
    rowSelectTestId?: (rowNumber: number) => string;
  },
) {
  return columnHelper.display({
    id: 'selection',
    header: ({ table }) => {
      const isChecked =
        options?.isSelectAllPagesActive ||
        table.getIsAllPageRowsSelected() ||
        (table.getIsSomePageRowsSelected() && 'indeterminate');

      return (
        <Checkbox
          checked={isChecked}
          onCheckedChange={(value) => {
            // If isSelectAllPagesActive is true and we're unchecking, deactivate it
            if (options?.isSelectAllPagesActive && !value) {
              options.onSelectAllPagesChange?.(false);
              table.toggleAllRowsSelected(false);
            } else {
              table.toggleAllPageRowsSelected(!!value);
            }
          }}
          aria-label="Select all"
          className="translate-y-[2px]"
          data-testid={options?.selectAllTestId}
        />
      );
    },
    cell: ({ row, column }) => {
      if (options?.isPinned) {
        return (
          <PinnedComponent pinned="left" column={column}>
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Select row"
              className="translate-y-[2px]"
              disabled={options?.isSelectAllPagesActive}
              data-testid={options?.rowSelectTestId?.(row.index + 1)}
            />
          </PinnedComponent>
        );
      }

      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
          disabled={options?.isSelectAllPagesActive}
          data-testid={options?.rowSelectTestId?.(row.index + 1)}
        />
      );
    },
    size: 50,
  });
}

const DEFAULT_PAGINATION_OPTIONS = [10, 20, 30, 40, 50];

export function EntityTablePagination<TData>({
  table,
  options = DEFAULT_PAGINATION_OPTIONS,
  onSelectAllPagesChange,
  isSelectAllPagesActive = false,
  totalRowCount,
  leftComponents,
}: EntityTablePaginationProps<TData>) {
  const uniqueRowsPerPage = new Set<number>([...options, table.getState().pagination.pageSize].map((item) => item));

  const [pageCount, setPageCount] = useState(table.getPageCount());

  useEffect(() => {
    if (table.getRowCount() === 0) {
      return;
    }
    setPageCount(table.getPageCount());
  }, [table.getRowCount(), table.getState().pagination]);

  const selectedCount = table.getFilteredSelectedRowModel().rows.length;
  const totalCount = table.getFilteredRowModel().rows.length;
  const isAllPageRowsSelected = table.getIsAllPageRowsSelected();

  const handleSelectAllPages = () => {
    if (isSelectAllPagesActive) {
      // Deselect all pages state and clear selection
      onSelectAllPagesChange?.(false);
      table.toggleAllRowsSelected(false);
    } else {
      // Activate select all pages state
      onSelectAllPagesChange?.(true);
    }
  };

  return (
    <div className="flex items-center justify-between px-2 mb-2">
      <div className="flex flex-1 items-center gap-4 text-sm text-muted-foreground">
        {leftComponents}
        {(selectedCount > 0 || isSelectAllPagesActive) && (
          <div className="flex items-center gap-2">
            <span>
              {isSelectAllPagesActive ? `All rows selected.` : `${selectedCount} of ${totalCount} row(s) selected.`}
            </span>
            {onSelectAllPagesChange && isAllPageRowsSelected && !isSelectAllPagesActive && (
              <button onClick={handleSelectAllPages} className="text-primary hover:underline font-medium">
                Select all {totalRowCount} rows
              </button>
            )}
            {onSelectAllPagesChange && isSelectAllPagesActive && (
              <button onClick={handleSelectAllPages} className="text-primary hover:underline font-medium">
                Deselect all {totalRowCount} rows
              </button>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]" data-testid={TABLE.pagination.rowsPerPage}>
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {Array.from(uniqueRowsPerPage).map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div
          className="flex w-[100px] items-center justify-center text-sm font-medium"
          data-testid={TABLE.pagination.pageInfo}
        >
          Page {table.getState().pagination.pageIndex + 1} of {pageCount || 1}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            data-testid={TABLE.pagination.first}
          >
            <span className="sr-only">Go to first page</span>
            <ArrowLeftFromLine className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            data-testid={TABLE.pagination.prev}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            data-testid={TABLE.pagination.next}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            data-testid={TABLE.pagination.last}
          >
            <span className="sr-only">Go to last page</span>
            <ArrowRightFromLine className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function EntityTable<RowData, GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey>({
  table,
  filters,
  filterBarClassName,
  filterLeftClassName,
  emptyState,
  errorState,
  hasError = false,
  isLoading = false,
  showPagination = false,
  paginationOptions,
  paginationLeftComponents,
  loadingState = <LoaderIcon />,
  bulkActionsMenuItems,
  topBarRightComponents,
  rowClassName,
  headerClassName,
  emptyStateClassName,
  topBarBottomComponents,
  onSelectAllPagesChange,
  isSelectAllPagesActive,
  totalRowCount,
  hideEndMessage,
  tableKey,
  autoManagedColumnIds,
  groupBy,
  rowTestId,
  cellTestId,
  endMessageTestId,
  bulkActionsTestId,
  'data-testid': dataTestId,
}: EntityTableProps<RowData, GroupKey>) {
  const [pinnedColumns] = useLocalStorage<PinnedColumnsState>('pinnedColumns', {});
  const { pathname } = useLocation();

  // Select all rows on current page when isSelectAllPagesActive is true
  useEffect(() => {
    if (isSelectAllPagesActive) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [table, isSelectAllPagesActive, table.getState().pagination.pageIndex]);

  const rowCount = table.getRowModel().rows.length;
  const isEmpty = rowCount === 0;
  const resolvedEmptyState =
    emptyState == null ? (
      <DefaultEmptyTable />
    ) : typeof emptyState === 'string' ? (
      <DefaultEmptyTable>{emptyState}</DefaultEmptyTable>
    ) : (
      emptyState
    );
  const columnCount = table.getVisibleFlatColumns().length;
  const renderTopBar = filters || bulkActionsMenuItems || topBarRightComponents || topBarBottomComponents;
  const loadingComponent = (
    <TableRow>
      <TableCell colSpan={columnCount}>
        <div className="flex justify-center">{loadingState}</div>
      </TableCell>
    </TableRow>
  );

  const errorComponent = (
    <TableRow>
      <TableCell colSpan={columnCount}>
        <div className="flex justify-center">{errorState}</div>
      </TableCell>
    </TableRow>
  );

  useEffect(() => {
    const key = tableKey ?? pathname;
    const storedPins = pinnedColumns[key];

    if (!storedPins) {
      return;
    }

    const userPinnedIds = storedPins.map((item) => item[0]);

    // Auto-managed columns (e.g. selection checkbox, edit icon) are pinned automatically
    // whenever any user-pinned column exists, always placed to the left of user pins.
    const existingManagedIds = (autoManagedColumnIds ?? []).filter((id) => table.getColumn(id) != null);

    // Exclude managed column IDs and stale column IDs (no longer in the table) from the
    // user list to prevent duplicates and unexpected auto-pinning from leftover localStorage entries.
    const nonManagedUserPinnedIds = userPinnedIds.filter(
      (id) => !existingManagedIds.includes(id) && table.getColumn(id) != null,
    );

    const effectivePinnedIds =
      nonManagedUserPinnedIds.length > 0
        ? [...existingManagedIds, ...nonManagedUserPinnedIds]
        : nonManagedUserPinnedIds;

    table.setState((prev) => ({
      ...prev,
      columnPinning: {
        left: effectivePinnedIds,
        right: [],
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedColumns, table, tableKey, pathname, (autoManagedColumnIds ?? []).join('|')]);

  return (
    <div className="h-full p-2 space-y-4 min-w-0 flex flex-col">
      {renderTopBar && (
        <>
          <div className={cn('flex items-center justify-between', filterBarClassName)}>
            <div className={cn('flex flex-1 items-center space-x-2', filterLeftClassName)}>
              {filters && filters.filterLeftComponents}
            </div>
            <div className="flex items-center space-x-1">
              {bulkActionsMenuItems && (
                <TableBulkActionsMenu
                  disabled={getCurrentRowSelectionCount(table) === 0 && !isSelectAllPagesActive}
                  selectedRowsCount={getCurrentRowSelectionCount(table)}
                  isSelectAllPagesActive={isSelectAllPagesActive}
                  totalRowCount={totalRowCount}
                  triggerTestId={bulkActionsTestId}
                >
                  {bulkActionsMenuItems}
                </TableBulkActionsMenu>
              )}
              {topBarRightComponents}
            </div>
          </div>
          {topBarBottomComponents && topBarBottomComponents}
        </>
      )}

      <>
        <div className={`overflow-auto flex-1 min-h-0 relative bg-background`}>
          <div className={'rounded-md border border-t-0 min-w-max'}>
            <Table data-testid={dataTestId}>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        header={header}
                        headerClassName={headerClassName}
                        tableKey={tableKey}
                        autoManagedColumnIds={autoManagedColumnIds}
                      />
                    ))}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {isLoading && loadingComponent}
                {!isLoading &&
                  (hasError ? (
                    errorComponent
                  ) : (
                    <>
                      {!isEmpty &&
                        (() => {
                          const rows = table.getRowModel().rows;

                          const renderRow = (row: (typeof rows)[number], rowIndex: number) => (
                            <TableRow
                              key={row.id}
                              data-state={row.getIsSelected() && 'selected'}
                              data-testid={rowTestId ? rowTestId(row.id, rowIndex) : undefined}
                            >
                              {row.getVisibleCells().map((cell) => (
                                <TableCell
                                  key={cell.id}
                                  className={cn(rowClassName, cell.column.columnDef.meta?.getCellClassName?.(cell.row))}
                                  style={{ ...getCommonPinningStyles(cell.column) }}
                                  data-testid={cellTestId ? cellTestId(row.id, cell.column.id, rowIndex) : undefined}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                          );

                          if (!groupBy) return rows.map((row, rowIndex) => renderRow(row, rowIndex));

                          let renderedRowIndex = 0;
                          return groupRowsByKey(rows, (row) => groupBy.getGroupKey(row.original)).flatMap(
                            ({ groupKey, rows: groupRows }) => [
                              <TableRow
                                key={`divider-${groupKey.key}`}
                                className="relative z-10 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.1)]"
                              >
                                <TableCell colSpan={columnCount} className="bg-muted/50 p-0 font-medium">
                                  <div className="sticky left-0 w-fit px-2 py-2">
                                    {groupBy.renderDivider(groupKey, groupRows.length)}
                                  </div>
                                </TableCell>
                              </TableRow>,
                              ...groupRows.map((row) => renderRow(row, renderedRowIndex++)),
                            ],
                          );
                        })()}
                      {!isEmpty && rowCount < 5 && !hideEndMessage && (
                        <TableRow>
                          <TableCell colSpan={columnCount}>
                            <div className="flex p-2" data-testid={endMessageTestId}>
                              No more results
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
              </TableBody>
            </Table>
          </div>
          {isEmpty && !isLoading && (
            <div
              className={cn(
                'sticky left-0 right-0 top-1/2 -translate-y-1/2 z-50 mt-20 overflow-visible pointer-events-none',
                emptyStateClassName,
              )}
            >
              {resolvedEmptyState}
            </div>
          )}
        </div>
        {showPagination && (
          <EntityTablePagination
            table={table}
            options={paginationOptions}
            onSelectAllPagesChange={onSelectAllPagesChange}
            isSelectAllPagesActive={isSelectAllPagesActive}
            totalRowCount={totalRowCount}
            leftComponents={paginationLeftComponents}
          />
        )}
      </>
    </div>
  );
}
